import {
  Badge,
  Button,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { ArrowRightIcon } from "lucide-react";
import type { PartnerProjectSaleListItemDTO } from "@/modules/packages/domain/contracts";
import { formatDate } from "@/shared/lib/date-time";

type PartnerPendingLockTableProps = {
  isLoading: boolean;
  data: PartnerProjectSaleListItemDTO[];
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onPaginationChange: (offset: number, limit: number) => void;
  onSortingChange: (id: string, desc: boolean) => void;
  onLockPricing: (id: string) => void;
};

function formatPricingStatus(status: PartnerProjectSaleListItemDTO["pricingStatus"]) {
  if (status === "draft") return "Draft";
  if (status === "quoted") return "Quoted";
  return status;
}

function formatPendingAmount(row: PartnerProjectSaleListItemDTO) {
  if (row.pricingStatus === "draft") {
    if (row.provisionalTotal != null) {
      return `${row.provisionalTotal.toLocaleString("lo-LA")} ກີບ (ປະມານ)`;
    }
    return "—";
  }

  const amount = row.finalPrice ?? row.price;
  if (amount != null) {
    return `${amount.toLocaleString("lo-LA")} ກີບ`;
  }
  return "—";
}

export function PartnerPendingLockTable({
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onPaginationChange,
  onSortingChange,
  onLockPricing,
}: PartnerPendingLockTableProps) {
  const columns: TanstackReactTable.ColumnDef<PartnerProjectSaleListItemDTO>[] =
    [
      createSortableColumn<PartnerProjectSaleListItemDTO>(
        "partnerLabel",
        "Partner",
        {
          size: 180,
          cell: ({ row }) => (
            <span className="font-medium">
              {row.original.partnerLabel?.trim() || "—"}
            </span>
          ),
        },
      ),
      createSortableColumn<PartnerProjectSaleListItemDTO>(
        "pricingStatus",
        "ສະຖານະ",
        {
          size: 100,
          cell: ({ row }) => (
            <Badge
              variant={
                row.original.pricingStatus === "quoted" ? "default" : "secondary"
              }
            >
              {formatPricingStatus(row.original.pricingStatus)}
            </Badge>
          ),
        },
      ),
      {
        id: "period",
        header: "ໄລຍະໂຄງການ",
        size: 180,
        cell: ({ row }) => (
          <span className="text-muted-foreground text-sm">
            {row.original.startDate} → {row.original.expireDate}
          </span>
        ),
      },
      {
        id: "amount",
        header: "ຍອດ",
        size: 160,
        cell: ({ row }) => (
          <span className="tabular-nums">{formatPendingAmount(row.original)}</span>
        ),
      },
      {
        id: "classCount",
        header: "ຄລາດສ໌",
        size: 72,
        cell: ({ row }) => (
          <span className="text-muted-foreground text-sm">
            {row.original.classCount}
          </span>
        ),
      },
      createSortableColumn<PartnerProjectSaleListItemDTO>(
        "createdAt",
        "ວັນທີບັນທຶກ",
        {
          size: 120,
          cell: ({ row }) => (
            <span className="text-muted-foreground text-sm">
              {formatDate(row.original.createdAt)}
            </span>
          ),
        },
      ),
      {
        id: "actions",
        header: "",
        size: 160,
        cell: ({ row }) => (
          <Button
            type="button"
            size="sm"
            className="bg-red-600 text-white hover:bg-red-700"
            onClick={() => onLockPricing(row.original.id)}
          >
            <ArrowRightIcon className="mr-1 size-4" />
            Lock ລາຄາ
          </Button>
        ),
      },
    ];

  return (
    <DataTable
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      onSortingChange={(sorting) => {
        if (!sorting || sorting.length === 0) return;
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id ?? "", !!sorting[0]?.desc);
      }}
    />
  );
}
